import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import msalInstance from './config/msal';
import { ToastContainer } from 'react-toastify';
import { Header } from '@/components/Header';
import { Dashboard } from '@/components/Dashboard';
import { PromptDetail } from '@/components/PromptDetail';
import { AuthPage } from '@/components/auth/AuthPage';
import VerifyEmailPage from '@/components/auth/VerifyEmailPage';
import VerifyNewEmailPage from '@/components/auth/VerifyNewEmailPage';
import SSOCallback from '@/components/auth/SSOCallback';
import WaitlistPage from '@/components/WaitlistPage';
import SettingsPage from '@/components/SettingsPage';
import BillingPage from '@/components/BillingPage';
import UpdateEmailPage from '@/components/UpdateEmailPage';
import VerifyEmailUpdatePage from '@/components/VerifyEmailUpdatePage';
import { useAuth, AuthProvider } from '@/contexts/AuthContext';
import { CategoryProvider } from './contexts/CategoryContext';
import { SearchProvider } from './contexts/SearchContext';
import { VoteProvider } from '@/contexts/VoteContext';
import { onAuthRequired } from '@/utils/auth';
import '@/utils/clearCookies'; // Make cookie clearing utilities available globally
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Protected Route wrapper component
const ProtectedRoute: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  const { isLoggedIn, isInitialized } = useAuth();

  // Show loading spinner while authentication state is being checked
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  // Only redirect to auth page if we've confirmed the user is not logged in
  if (!isLoggedIn) {
    // Save current route for redirect after login
    const currentPath = window.location.pathname;
    if (currentPath !== '/auth' && !currentPath.startsWith('/auth/')) {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    onAuthRequired();
    return <Navigate to="/auth" />;
  }

  return <>{children}</>;
};

const AppContent = () => {
  const location = useLocation();
  const hideHeaderRoutes = ['/teams'];
  return (
    <div className="min-h-screen bg-gray-50">
      {!hideHeaderRoutes.includes(location.pathname) && <Header />}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick pauseOnHover />
      <main className="container mx-auto px-4 lg:px-8 pt-4 pb-8">
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/:mode" element={<AuthPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/verify-new-email" element={<VerifyNewEmailPage />} />
          <Route path="/auth/callback" element={<SSOCallback />} />
          <Route path="/login" element={<Navigate to="/auth/login" />} />
          <Route path="/signup" element={<Navigate to="/auth/signup" />} />
          <Route path="/teams" element={<WaitlistPage />} />
          {/* Dashboard and prompt routes - keep all logic from both branches */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prompts/:id"
            element={
              <ProtectedRoute>
                <PromptDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/update-email"
            element={
              <ProtectedRoute>
                <UpdateEmailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/verify-email-update"
            element={
              <ProtectedRoute>
                <VerifyEmailUpdatePage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <AuthProvider>
          <VoteProvider>
            <SearchProvider>
              <CategoryProvider>
                <AppContent />
              </CategoryProvider>
            </SearchProvider>
          </VoteProvider>
        </AuthProvider>
      </Router>
    </MsalProvider>
  );
}

export default App;
